import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "./Analytics.css";

import {
  getAcquisitionAnalytics,
  getAnalyticsSummary,
  getContentAnalytics,
  getEngagementAnalytics,
  getFunnelAnalytics,
  refreshAnalytics,
} from "../../api/lib/analytics";
import {
  ChartDefs,
  Pill,
  StatCard,
  deriveTrend,
  formatNumber,
  formatRate,
  isSmallSample,
  readDate,
  shortDate,
} from "../../components/analytics/primitives";
import { deriveFinding } from "./finding";
import {
  AcquisitionSection,
  ContentSection,
  CreationSection,
  RetentionSection,
} from "./sections";

/**
 * Product analytics dashboard.
 *
 * A single scrolling page whose sections follow the parent's journey — arrive,
 * make something, what they made it with, whether they come back — rather than
 * grouping by the system that produced the numbers.
 *
 * Data comes from BigQuery views of the app's Firebase Analytics events, via
 * `/v1/admin/analytics/*`. Every endpoint is fetched once per filter change and
 * cached server-side, so all sections show the same moment in time.
 *
 * Rendered outside the admin nav shell: that shell centres its children in a
 * fixed-height box with no overflow, which a tall dashboard cannot live in.
 */

const RANGES = [
  { id: "7", label: "Last 7 days", days: 7 },
  { id: "28", label: "Last 28 days", days: 28 },
  { id: "90", label: "Last 90 days", days: 90 },
  { id: "all", label: "All time", days: null },
];

const isoDaysAgo = (days) => {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return date.toISOString().slice(0, 10);
};

const Analytics = () => {
  const navigate = useNavigate();

  const [range, setRange] = useState("28");
  const [platform, setPlatform] = useState("");

  const [data, setData] = useState(null);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const filters = useMemo(() => {
    const days = RANGES.find((r) => r.id === range)?.days;
    return {
      from: days ? isoDaysAgo(days) : null,
      to: null,
      platform: platform || null,
    };
  }, [range, platform]);

  // Outside the nav shell, so this route guards itself.
  useEffect(() => {
    if (!localStorage.getItem("user")) navigate("/");
  }, [navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [summary, funnel, acquisition, content, engagement] = await Promise.all([
        getAnalyticsSummary(filters),
        getFunnelAnalytics(filters),
        getAcquisitionAnalytics(filters),
        getContentAnalytics(filters),
        getEngagementAnalytics(filters),
      ]);
      setMeta(summary?.data?.meta ?? null);
      setData({
        summary: summary?.data?.data ?? null,
        funnel: funnel?.data?.data ?? {},
        acquisition: acquisition?.data?.data ?? {},
        content: content?.data?.data ?? {},
        engagement: engagement?.data?.data ?? {},
      });
    } catch (err) {
      // Usually means the server has no BigQuery credentials — a deployment
      // state, not a bug. Saying so beats an empty dashboard that reads as
      // "no users".
      setError(
        err?.response?.data?.message ||
          "Could not load analytics. Check the API is running and reachable."
      );
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    load();
  }, [load]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await refreshAnalytics();
      await load();
      toast.success("Refreshed from BigQuery");
    } catch {
      toast.error("Refresh failed");
    } finally {
      setRefreshing(false);
    }
  };

  const summary = data?.summary;
  const period = summary?.period;
  const prev = summary?.previous;

  const freshness = meta?.freshness;

  const rangeLabel = RANGES.find((r) => r.id === range)?.label.toLowerCase();
  const comparisonHint = prev ? `vs previous period` : rangeLabel;

  return (
    <div className="analytics">
      <ChartDefs />
      <ToastContainer />

      <div className="wrap">
        <header className="page">
          <div className="title">
            <Link to="/dashboard" className="back">
              ← Back to dashboard
            </Link>
            <h1>Product analytics</h1>
            <p>How parents discover templates, make videos, and come back.</p>
            {/* Stated rather than assumed: the daily export lands about a day
                behind and results are cached on top of that, so without this a
                reader takes the page for live. */}
            {freshness && (
              <p className="freshness">
                <span className={`freshness__dot${freshness.days_behind > 2 ? " is-stale" : ""}`} />
                Data through <strong>{shortDate(readDate(freshness.data_through))}</strong>
                {freshness.days_behind > 0 && ` · ${freshness.days_behind} day${freshness.days_behind === 1 ? "" : "s"} behind`}
                {" · "}
                {freshness.days_of_history} day{freshness.days_of_history === 1 ? "" : "s"} of history
              </p>
            )}
          </div>

          <div className="controls">
            <select value={range} onChange={(e) => setRange(e.target.value)} aria-label="Date range">
              {RANGES.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.label}
                </option>
              ))}
            </select>
            <select
              value={platform}
              onChange={(e) => setPlatform(e.target.value)}
              aria-label="Platform"
            >
              <option value="">All platforms</option>
              <option value="IOS">iOS</option>
              <option value="ANDROID">Android</option>
            </select>
            <button
              type="button"
              className="control--primary"
              onClick={handleRefresh}
              disabled={refreshing || loading}
            >
              {refreshing ? "Refreshing…" : "Refresh"}
            </button>
          </div>
        </header>

        {error ? (
          <div className="state state--error">
            <p>{error}</p>
            <button type="button" onClick={load}>
              Try again
            </button>
          </div>
        ) : loading ? (
          <div className="state">Loading analytics…</div>
        ) : (
          <>
            {/* The finding, in plain language, before any chart. An executive
                should not have to read a funnel to learn what is wrong. */}
            {(() => {
              const finding = deriveFinding({ summary, funnel: data.funnel });
              return (
                <div className={`finding finding--${finding.tone}`}>
                  <Pill tone={finding.tone}>{finding.label}</Pill>
                  <div className="finding__body">
                    <h2>{finding.headline}</h2>
                    <p>{finding.detail}</p>
                  </div>
                </div>
              );
            })()}

            {/* Period figures with the equivalent window before them, so each
                number carries a direction rather than sitting on its own. */}
            <div className="kpis">
              <StatCard
                label="Active parents"
                value={formatNumber(period?.active_users)}
                hint={comparisonHint}
                trend={deriveTrend(period?.active_users, prev?.active_users)}
              />
              <StatCard
                label="New parents"
                value={formatNumber(period?.new_users)}
                hint={comparisonHint}
                trend={deriveTrend(period?.new_users, prev?.new_users)}
              />
              <StatCard
                label="Started recording"
                value={formatNumber(period?.started_recording)}
                hint={comparisonHint}
                trend={deriveTrend(period?.started_recording, prev?.started_recording)}
              />
              {/* Rate, so it is suppressed to raw counts while the sample is
                  too small for a percentage to mean anything. */}
              <StatCard
                label="Videos saved"
                value={formatRate(period?.completed_upload, period?.started_upload)}
                hint={
                  isSmallSample(period?.started_upload)
                    ? "Too few attempts for a percentage"
                    : "Should stay above 95%"
                }
                trend={deriveTrend(period?.upload_success_rate, prev?.upload_success_rate)}
              />
            </div>

            {/* Lifetime context, kept separate from the period figures above so
                the two are never read as the same thing. */}
            <p className="lifetime">
              All time: <strong>{formatNumber(summary?.total_users)}</strong> parents,{" "}
              <strong>{formatNumber(summary?.activated_users)}</strong> of whom have made a
              video ({formatRate(summary?.activated_users, summary?.total_users)}).
            </p>
          </>
        )}
      </div>

      {/* Outside .wrap so each section's background runs edge to edge; the
          content inside is re-centred by .section__inner. */}
      {!error && !loading && data && (
        <>
          <AcquisitionSection data={data.acquisition} summary={summary} />
          <CreationSection data={data.funnel} summary={summary} />
          <ContentSection data={data.content} />
          <RetentionSection data={data.engagement} />

          <section className="section section--reading" id="reading" aria-labelledby="h-reading">
              <div className="section__inner">
              <div className="section__head">
                <div className="section__title">
                  <p className="section__q">What these numbers do and don't say</p>
                  <h2 id="h-reading">How to read this</h2>
                </div>
              </div>
              <div className="caveats">
                <h4>Before you act on a number</h4>
                <ul>
                  <li>
                    Everything here counts <strong>people, not actions</strong>. A parent who
                    tries to save the same video three times counts once, which is why no
                    percentage goes above 100%.
                  </li>
                  <li>
                    We only started collecting recently, so anyone who joined before then has
                    no early history. Groups based on when someone signed up will look thin
                    until enough people have joined since.
                  </li>
                  <li>
                    When saving fails, this page tells you <strong>which step</strong> broke —
                    not why. The engineering team gets the technical detail separately, on
                    purpose, so this page stays about people rather than error codes.
                  </li>
                  <li>
                    Figures about people coming back only appear once enough time has passed. A
                    blank month-later figure means the month is not up yet — not that nobody
                    returned.
                  </li>
                  <li>
                    How someone signed in is the button they pressed. It does not tell us where
                    they heard about Neemo — nothing currently tracks that.
                  </li>
                </ul>
              </div>
              </div>
          </section>
        </>
      )}
    </div>
  );
};

export default Analytics;
